import Auth from '@aws-amplify/auth';
import * as Sentry from '@sentry/browser';
import { Checkbox } from '@/widgets/components/checkbox';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { AlertMsgFormFieldMissingEmailPassword } from '@/alert-string-constants';
import { LOGIN_MENU_TYPES } from '@/constants';
import * as userActions from '@/user/user-actions';
import { MappedAlerts } from '@/widgets/mapped-alerts';
import {
  InputWithTitle,
  RentalscapeLogoPlusTitle,
} from './common-login-components';
import { LoginFormHeader } from './login-form-header';
import * as login from './login-form-styles';
import { CognitoLoginMgr } from '../cognito-login-manager/cognito-login-mgr';
import { toaster } from '@/utils/toaster';

const COGNITO_NEW_PASSWORD_CHALLENGE = 'NEW_PASSWORD_REQUIRED';

window.Auth = Auth;

/**
 * This method is a helper for managing component state in case of a signin challenge
 *
 * @param {CognitoUser} user the cognito user that's trying to signin
 * @param {func} onChallengeCallback callback function used to update the state of a component
 *  in the event of a NEW_PASSWORD_REQUIRED challenge
 * @param {func} onSuccessCallback callback for when the user has successfully logged in
 */
const onSignInAttempt = (props, isSSOSignInAttempt = false) => {
  const { user, setIsAuthenticated, onLoginSuccess, newPasswordRequired } =
    props;

  if (user.challengeName === COGNITO_NEW_PASSWORD_CHALLENGE) {
    // Force change password case
    newPasswordRequired();
  } else if (user.signInUserSession.isValid()) {
    // Login success case
    setIsAuthenticated(user);
    // Fetch current session, retrieve groups from id Token, save in state
    // getUserGroups();

    /**
     * Capture the login event here as refreshing the client will trigger the onLoginSuccessSaga
     *  and autoLoginAttemptSaga (which also has this same captureEvent), so we don't send the
     *  same event twice to Sentry
     */
    Sentry.configureScope((scope) => {
      scope.setUser({ username: user.username });
    });
    Sentry.captureMessage(`User login event by ${user.username}`);
    onLoginSuccess(user);
  } else {
    // Login fails
    const message = isSSOSignInAttempt
      ? 'Failed to login via SSO.'
      : 'Failed to login.';
    toaster.error(message, 10000);

    Sentry.captureMessage(`onSignInAttempt Capture: ${message}`, {
      contexts: { user, isSSOSignInAttempt },
    });
    // MappedAlerts.error(ERROR_TYPES.NetworkConnectivityException);
  }
};

/**
 * This method is a helper for handling a login failure (e.g.: wrong password)
 * @param {*} err
 */
const onSignInFail = (err) => {
  MappedAlerts.error(err.code);
};

function CheckboxForgotPasswordGroup(props) {
  return (
    <login.CheckboxForgotPasswordWrapper>
      <login.CheckboxWithTitleWrapper>
        <Checkbox
          checked={props.rememberMe}
          onChange={() => props.setRememberMe(!props.rememberMe)}
        />
        <login.StyledText padding="2px 0px 0px 21px">
          Remember me
        </login.StyledText>
      </login.CheckboxWithTitleWrapper>

      <login.StyledForgotPasswordButton
        onClick={props.forgotPasswordClicked}
        padding="0px"
        data-cy="forgot-password-button"
      >
        Reset password
      </login.StyledForgotPasswordButton>
    </login.CheckboxForgotPasswordWrapper>
  );
}

const loginModes = {
  SSO: 'SSO',
  DEFAULT: 'DEFAULT',
  INIT: 'INIT',
};

export function SignInFormComponent(props) {
  const { authFadeOut, showOtherForm } = props;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [verifyingCred, setVerifyingCred] = useState(false);
  const [loginMode, setLoginMode] = useState(loginModes.INIT);
  const [disableEmail, setDisableEmail] = useState(false);

  useEffect(() => {
    const rememberMeUsername = window.localStorage.getItem('username');
    if (rememberMeUsername) {
      setUsername(rememberMeUsername);
      setRememberMe(true);
    }
  }, []);

  const handleRememberMe = () => {
    if (rememberMe) {
      localStorage.setItem('username', username);
    } else {
      localStorage.removeItem('username');
    }
  };

  const handleSSOLoginAttempt = useCallback(async (email) => {
    /*
     * If here, means the user was redirected to the sign in callback URL.
     * We'll attempt to log the user in
     */
    await CognitoLoginMgr.getInstance().amplifyConfigure(email);
    const user = await Auth.currentAuthenticatedUser();
    onSignInAttempt({ user, ...props }, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUserClickLogin = async () => {
    const unameLength = username.length;
    const pwordLength = password.length;
    if (unameLength > 0 && pwordLength > 0) {
      setVerifyingCred(true);
      handleRememberMe();
      try {
        await CognitoLoginMgr.getInstance().amplifyConfigure(username);
        const user = await Auth.signIn(
          username.toLowerCase().trim(),
          password.trim()
        );
        await onSignInAttempt({ user, ...props });
      } catch (err) {
        setVerifyingCred(false);
        onSignInFail(err);
      }
    } else {
      // ! to prevent multiple instances
      toaster.error(AlertMsgFormFieldMissingEmailPassword);
    }
  };

  const handleForgetPasswordClick = useCallback(async () => {
    props.handleForgotPasswordClicked();
    await CognitoLoginMgr.getInstance().amplifyConfigure(username);
  }, [props, username]);

  const checkSSOCB = useCallback(() => {
    const isSSOEmail = CognitoLoginMgr.getInstance().isSSOEmail(username);
    if (isSSOEmail) {
      const loginUrl = CognitoLoginMgr.getInstance().getSSOLoginUrl(username);
      window.location.href = loginUrl;
    } else {
      setLoginMode(loginModes.DEFAULT);
      props.updateCurrentMenu(LOGIN_MENU_TYPES.SIGN_IN);
      setDisableEmail(true);
    }
  }, [props, username]);

  const handleKeyUp = (evt) => {
    if (evt.key === 'Enter' && loginMode === loginModes.DEFAULT)
      handleUserClickLogin();
    else if (evt.key === 'Enter' && loginMode !== loginModes.DEFAULT)
      checkSSOCB();
  };

  useEffect(() => {
    /**
     * This doesn't seem to do anything.
     * "autoConfigureAmplify" in "user-sagas.js" is where SSO logins
     * are actually handled.
     */
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    const {
      state: ssoEmail, // "state" => email of logged in user
    } = params;

    if (ssoEmail) handleSSOLoginAttempt(ssoEmail);
  }, [handleSSOLoginAttempt]);

  return (
    <login.SignupFormAnimationWrapper
      id="sign-in-animator"
      authFadeOut={authFadeOut}
      showOtherForm={showOtherForm}
    >
      {loginMode === loginModes.DEFAULT && (
        <LoginFormHeader
          goBackEnabled={true}
          goBack={() => (window.location = '/login')}
        />
      )}

      {loginMode === loginModes.INIT && <login.StyledSSOSpacer />}

      <RentalscapeLogoPlusTitle paddingBottom={'0px'} />
      <login.PoweredByDeckard />
      <InputWithTitle
        value={username}
        focus={true}
        onInput={(e) => setUsername(e.target.value)}
        error={+!username}
        onKeyUp={handleKeyUp}
        text="Email"
        disabled={disableEmail}
      />

      {loginMode !== loginModes.DEFAULT && !showOtherForm && (
        <login.StyledSSOButton data-cy="login-next-button" onClick={checkSSOCB}>
          Next
        </login.StyledSSOButton>
      )}

      {loginMode === loginModes.DEFAULT && !showOtherForm && (
        <>
          <InputWithTitle
            isPasswordInput
            value={password}
            onInput={(e) => setPassword(e.target.value)}
            error={+!password}
            text="Password"
            onKeyUp={handleKeyUp}
          />
          <CheckboxForgotPasswordGroup
            rememberMe={rememberMe}
            setRememberMe={setRememberMe}
            forgotPasswordClicked={handleForgetPasswordClick}
          />

          <login.StyledFormSubmitButton
            loading={verifyingCred}
            onClick={handleUserClickLogin}
            data-cy="login-button"
          >
            Login
          </login.StyledFormSubmitButton>
        </>
      )}
    </login.SignupFormAnimationWrapper>
  );
}

function mapDispatchToProps(dispatch) {
  return {
    setIsAuthenticated: (cognitoUser) =>
      dispatch(userActions.setIsAuthenticated({ cognitoUser })),
    onLoginSuccess: (cognitoUser) =>
      dispatch(userActions.onLoginSuccess({ cognitoUser })),
  };
}

export const SignInForm = connect(
  null,
  mapDispatchToProps
)(SignInFormComponent);
